import axios from 'axios';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Module-level variable set by ChurchContext — injecte church_id pour tout
// rôle pouvant basculer entre plusieurs églises (superadmin_platform ou
// superviseur), pas seulement le superadmin.
let _activeChurchId = null;
let _canSwitchChurch = false;

export function setApiChurchFilter(churchId, canSwitchChurch) {
  _activeChurchId = churchId;
  _canSwitchChurch = canSwitchChurch;
}

// Endpoints that should NOT get the church_id filter
const SKIP_CHURCH_FILTER = [
  '/churches',
  '/auth/',
  '/token',
  '/notifications/email-config',
  '/notifications/audit-logs',
  '/notifications/export',
  '/notifications/church-settings',
  '/notifications/whatsapp/',
];

// Request interceptor to add JWT token + church_id filter
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Auto-inject church_id for superadmin_platform
  if (_canSwitchChurch && _activeChurchId) {
    const skip = SKIP_CHURCH_FILTER.some(path => config.url?.includes(path));
    if (!skip) {
      if (config.method === 'get') {
        config.params = { ...(config.params || {}), church_id: _activeChurchId };
      } else if (['post', 'put', 'patch'].includes(config.method)) {
        if (config.data instanceof FormData) {
          // FormData (upload de fichier) : ajouter church_id comme champ, ne pas toucher au Content-Type
          config.data.append('church_id', _activeChurchId);
        } else {
          // Injecter church_id dans le body JSON pour les mutations
          try {
            const data = typeof config.data === 'string' ? JSON.parse(config.data) : (config.data || {});
            data.church_id = _activeChurchId;
            config.data = JSON.stringify(data);
            config.headers['Content-Type'] = 'application/json';
          } catch (e) { /* body non-JSON, on ne touche pas */ }
        }
      }
    }
  }

  return config;
});

// Response interceptor to handle token refresh
// Si plusieurs requêtes échouent en même temps avec un token expiré, on ne
// déclenche qu'un seul appel /token/refresh/ et les autres attendent son résultat
// (évite que les appels concurrents utilisent un refresh token déjà tourné/invalidé).
let refreshPromise = null;

// Anti-spam : une seule notification "accès refusé" visible à la fois, même
// si plusieurs requêtes échouent en 403 en même temps (ex: chargement d'une
// page qui fait 3 appels en parallèle, tous refusés pour le même manque de droit).
let forbiddenToastVisible = false;
const notifyForbidden = () => {
  if (forbiddenToastVisible) return;
  forbiddenToastVisible = true;
  toast.error("Vous n'avez pas les droits nécessaires pour cette action. Contactez votre administrateur.", {
    duration: 5000,
    id: 'forbidden-action',
  });
  setTimeout(() => { forbiddenToastVisible = false; }, 5200);
};

// Anti-spam : une seule notification "trop de requêtes" visible à la fois.
let tooManyToastVisible = false;
const notifyTooMany = (retryAfter) => {
  if (tooManyToastVisible) return;
  tooManyToastVisible = true;
  const seconds = parseInt(retryAfter, 10);
  const wait = seconds > 0 ? ` Réessayez dans ${seconds < 60 ? `${seconds}s` : `${Math.ceil(seconds / 60)} min`}.` : ' Réessayez dans quelques instants.';
  toast.error(`Trop de requêtes envoyées.${wait}`, {
    duration: 6000,
    id: 'too-many-requests',
  });
  setTimeout(() => { tooManyToastVisible = false; }, 6200);
};

// Anti-spam : une seule notification "erreur serveur / réseau" visible à la fois.
// Une requête peut passer `silentError: true` dans sa config quand l'écran
// affiche déjà son propre message (ex: Espace Fidèle, cas D).
let serverErrorToastVisible = false;
const notifyServerError = (message) => {
  if (serverErrorToastVisible) return;
  serverErrorToastVisible = true;
  toast.error(message, { duration: 6000, id: 'server-error' });
  setTimeout(() => { serverErrorToastVisible = false; }, 6200);
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (!original?.silentError && !axios.isCancel(error)) {
      if (!error.response) {
        notifyServerError('Connexion au serveur impossible. Vérifiez votre connexion internet puis réessayez.');
        return Promise.reject(error);
      }
      if (error.response.status >= 500) {
        notifyServerError(error.response.data?.error || 'Une erreur interne est survenue. Veuillez réessayer ou contacter le support RCC Connect.');
        return Promise.reject(error);
      }
    }
    if (error.response?.status === 403) {
      notifyForbidden();
      return Promise.reject(error);
    }
    if (error.response?.status === 429) {
      notifyTooMany(error.response?.headers?.['retry-after']);
      return Promise.reject(error);
    }
    if (error.response?.status === 401 && !original._retry) {
      original._retry = true;
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          if (!refreshPromise) {
            refreshPromise = axios.post(`${API_BASE_URL}/token/refresh/`, {
              refresh: refreshToken,
            }).finally(() => { refreshPromise = null; });
          }
          const res = await refreshPromise;
          localStorage.setItem('access_token', res.data.access);
          original.headers.Authorization = `Bearer ${res.data.access}`;
          return api(original);
        } catch {
          localStorage.clear();
          window.location.href = '/login';
        }
      } else {
        localStorage.clear();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;
